/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { LocationType } from 'src/graphql/generated/graphql.generated';
import { AddressRecord, AddressProps } from 'src/store/generated/address-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface LocationProps {
  id: string;
  name: string;
  abbreviation: string;
  locationType: LocationType;
  address: AddressProps;
  timezoneCd: string | null;
  isRotorWingLz: boolean | null;
  notes: string | null;
  sourceSystemId: string | null;
  closedDate: Date | null;
  duplicateOfId: string | null;
  isLandingZoneOutOfService: boolean | null;
  landingZoneOutOfServiceNotes: string | null;
  landingZoneOutOfServiceExpirationDate: Date | null;
  landingZoneOutOfServiceReasonId: string | null;
  isPreferred: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class LocationRecord extends Record<LocationProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  locationType: null as any,
  address: null as any,
  timezoneCd: null as any,
  isRotorWingLz: null as any,
  notes: null as any,
  sourceSystemId: null as any,
  closedDate: null as any,
  duplicateOfId: null as any,
  isLandingZoneOutOfService: null as any,
  landingZoneOutOfServiceNotes: null as any,
  landingZoneOutOfServiceExpirationDate: null as any,
  landingZoneOutOfServiceReasonId: null as any,
  isPreferred: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationType: LocationType;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    notes: string | null;
    sourceSystemId: string | null;
    closedDate: string | number | Date | null;
    duplicateOfId: string | null;
    isLandingZoneOutOfService: boolean | null;
    landingZoneOutOfServiceNotes: string | null;
    landingZoneOutOfServiceExpirationDate: string | number | Date | null;
    landingZoneOutOfServiceReasonId: string | null;
    isPreferred: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): LocationRecord {
    if (json == null) {
      throw new Error('LocationRecord.fromJS: parameter cannot be null');
    }
    return new LocationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'LocationJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'LocationJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'LocationJson.abbreviation'),
      locationType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'locationType', LocationType.Unspecified),
        'LocationJson.locationType',
      ),
      address: new AddressRecord(getFromOrThrow(json, (j) => j.address, 'LocationJson.address')),
      timezoneCd: json.timezoneCd,
      isRotorWingLz: json.isRotorWingLz,
      notes: json.notes,
      sourceSystemId: json.sourceSystemId,
      closedDate: json.closedDate != null ? new Date(json.closedDate) : null,
      duplicateOfId: json.duplicateOfId,
      isLandingZoneOutOfService: json.isLandingZoneOutOfService,
      landingZoneOutOfServiceNotes: json.landingZoneOutOfServiceNotes,
      landingZoneOutOfServiceExpirationDate:
        json.landingZoneOutOfServiceExpirationDate != null
          ? new Date(json.landingZoneOutOfServiceExpirationDate)
          : null,
      landingZoneOutOfServiceReasonId: json.landingZoneOutOfServiceReasonId,
      isPreferred: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPreferred', false),
        'LocationJson.isPreferred',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationType: LocationType;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    notes: string | null;
    sourceSystemId: string | null;
    closedDate: string | number | Date | null;
    duplicateOfId: string | null;
    isLandingZoneOutOfService: boolean | null;
    landingZoneOutOfServiceNotes: string | null;
    landingZoneOutOfServiceExpirationDate: string | number | Date | null;
    landingZoneOutOfServiceReasonId: string | null;
    isPreferred: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [LocationRecord, Error[]] {
    if (json == null) {
      throw new Error('LocationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new LocationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'LocationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'LocationJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'LocationJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'locationType', LocationType.Unspecified),
            'LocationJson.locationType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address: tryOrDefault(
        () => new AddressRecord(getFromOrThrow(json, (j) => j.address, 'LocationJson.address')),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isRotorWingLz: tryOrDefault(
        () => json.isRotorWingLz,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceSystemId: tryOrDefault(
        () => json.sourceSystemId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      closedDate: tryOrDefault(
        () => (json.closedDate != null ? new Date(json.closedDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      duplicateOfId: tryOrDefault(
        () => json.duplicateOfId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isLandingZoneOutOfService: tryOrDefault(
        () => json.isLandingZoneOutOfService,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      landingZoneOutOfServiceNotes: tryOrDefault(
        () => json.landingZoneOutOfServiceNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      landingZoneOutOfServiceExpirationDate: tryOrDefault(
        () =>
          json.landingZoneOutOfServiceExpirationDate != null
            ? new Date(json.landingZoneOutOfServiceExpirationDate)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      landingZoneOutOfServiceReasonId: tryOrDefault(
        () => json.landingZoneOutOfServiceReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPreferred: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPreferred', false),
            'LocationJson.isPreferred',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: LocationProps) {
    super(props);
    if (props == null) {
      throw new Error('LocationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class LocationViewModelBase implements LocationProps {
  protected readonly _props: LocationProps;
  private _json?: LocationProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get locationType(): LocationType {
    return this._props.locationType;
  }
  get address(): AddressProps {
    return this._props.address;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get isRotorWingLz(): boolean | null {
    return this._props.isRotorWingLz;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get sourceSystemId(): string | null {
    return this._props.sourceSystemId;
  }
  get closedDate(): Date | null {
    return this._props.closedDate;
  }
  get duplicateOfId(): string | null {
    return this._props.duplicateOfId;
  }
  get isLandingZoneOutOfService(): boolean | null {
    return this._props.isLandingZoneOutOfService;
  }
  get landingZoneOutOfServiceNotes(): string | null {
    return this._props.landingZoneOutOfServiceNotes;
  }
  get landingZoneOutOfServiceExpirationDate(): Date | null {
    return this._props.landingZoneOutOfServiceExpirationDate;
  }
  get landingZoneOutOfServiceReasonId(): string | null {
    return this._props.landingZoneOutOfServiceReasonId;
  }
  get isPreferred(): boolean {
    return this._props.isPreferred;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAgency() {
    return this._props.locationType === LocationType.Agency;
  }
  public get isAirport() {
    return this._props.locationType === LocationType.Airport;
  }
  public get isBase() {
    return this._props.locationType === LocationType.Base;
  }
  public get isCommunicationsCenter() {
    return this._props.locationType === LocationType.CommunicationsCenter;
  }
  public get isCorporateOffice() {
    return this._props.locationType === LocationType.CorporateOffice;
  }
  public get isFbo() {
    return this._props.locationType === LocationType.Fbo;
  }
  public get isFuelTank() {
    return this._props.locationType === LocationType.FuelTank;
  }
  public get isHangar() {
    return this._props.locationType === LocationType.Hangar;
  }
  public get isLandingZone() {
    return this._props.locationType === LocationType.LandingZone;
  }
  public get isMaintenanceFacility() {
    return this._props.locationType === LocationType.MaintenanceFacility;
  }
  public get isMedicalFacility() {
    return this._props.locationType === LocationType.MedicalFacility;
  }
  public get isRadioTower() {
    return this._props.locationType === LocationType.RadioTower;
  }
  public get isRemoteMaintenanceFacility() {
    return this._props.locationType === LocationType.RemoteMaintenanceFacility;
  }
  public get isUnspecified() {
    return this._props.locationType === LocationType.Unspecified;
  }

  public isAnyLocationType(...locationType: LocationType[]) {
    return locationType.includes(this._props.locationType);
  }

  constructor(props: LocationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        locationType: this._props.locationType,
        address: this._props.address,
        timezoneCd: this._props.timezoneCd,
        isRotorWingLz: this._props.isRotorWingLz,
        notes: this._props.notes,
        sourceSystemId: this._props.sourceSystemId,
        closedDate: this._props.closedDate,
        duplicateOfId: this._props.duplicateOfId,
        isLandingZoneOutOfService: this._props.isLandingZoneOutOfService,
        landingZoneOutOfServiceNotes: this._props.landingZoneOutOfServiceNotes,
        landingZoneOutOfServiceExpirationDate: this._props.landingZoneOutOfServiceExpirationDate,
        landingZoneOutOfServiceReasonId: this._props.landingZoneOutOfServiceReasonId,
        isPreferred: this._props.isPreferred,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllLocationEntities = (state: AppState) => state.entityStores.location.entities;

export const selectLocationById = (id: string) => (state: AppState) =>
  state.entityStores.location.entities.get(id);

export const selectLocationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.location.meta.get(id);

export const selectLocationAndMetadataById = (id: string) =>
  createSelector(selectLocationById(id), selectLocationMetadataById(id), (location, meta) => ({
    location,
    meta,
  }));

export function selectLocationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllLocationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, LocationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectLocationEntitiesByName(name: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectLocationEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectLocationEntitiesByLocationType(locationType: LocationType) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.locationType === locationType).valueSeq(),
  );
}

export function selectLocationEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectLocationEntitiesByIsRotorWingLz(isRotorWingLz: boolean) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.isRotorWingLz === isRotorWingLz).valueSeq(),
  );
}

export function selectLocationEntitiesByNotes(notes: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectLocationEntitiesBySourceSystemId(sourceSystemId: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.sourceSystemId === sourceSystemId).valueSeq(),
  );
}

export function selectLocationEntitiesByClosedDate(closedDate: Date) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.closedDate === closedDate).valueSeq(),
  );
}

export function selectLocationEntitiesByDuplicateOfId(duplicateOfId: string) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.duplicateOfId === duplicateOfId).valueSeq(),
  );
}

export function selectLocationEntitiesByIsLandingZoneOutOfService(
  isLandingZoneOutOfService: boolean,
) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.isLandingZoneOutOfService === isLandingZoneOutOfService).valueSeq(),
  );
}

export function selectLocationEntitiesByLandingZoneOutOfServiceNotes(
  landingZoneOutOfServiceNotes: string,
) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities
      .filter((e) => e.landingZoneOutOfServiceNotes === landingZoneOutOfServiceNotes)
      .valueSeq(),
  );
}

export function selectLocationEntitiesByLandingZoneOutOfServiceExpirationDate(
  landingZoneOutOfServiceExpirationDate: Date,
) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities
      .filter(
        (e) => e.landingZoneOutOfServiceExpirationDate === landingZoneOutOfServiceExpirationDate,
      )
      .valueSeq(),
  );
}

export function selectLocationEntitiesByLandingZoneOutOfServiceReasonId(
  landingZoneOutOfServiceReasonId: string,
) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities
      .filter((e) => e.landingZoneOutOfServiceReasonId === landingZoneOutOfServiceReasonId)
      .valueSeq(),
  );
}

export function selectLocationEntitiesByIsPreferred(isPreferred: boolean) {
  return createSelector(selectAllLocationEntities, (entities) =>
    entities.filter((e) => e.isPreferred === isPreferred).valueSeq(),
  );
}
//#endregion Selectors
